import * as React from 'react';
import { BaSeI18nContext } from '../../contexts/i18n';
import { BaSeTheme } from '../../theme';
import { BaSeTooltip } from '../alerts/tooltip/tooltip';
import { BaSeIcon } from '../image/icon';
import { BaSeSmall1 } from '../typography/small/small1';
import {
  StepContentCollectionItem,
  StepperContentToRender,
} from './step-content';
import {
  FadableItem,
  StepActionProps,
  StepBehaviorProps,
  StepCircleHeader,
  StepColorProps,
  StepContainer,
  StepItem,
  StepOrientationProps,
  StepText,
  TypeTransitionProps,
  StepResumeOnClosed,
} from './stepper-styled';

export interface StepHeaderProps
  extends StepColorProps,
    StepOrientationProps,
    StepActionProps,
    StepBehaviorProps {
  steps: StepContentCollectionItem[];
  title?: string | string[];
  iconName?: string;
  tooltip?: 'none' | 'top' | 'bottom' | 'left' | 'right';
  currentStep: number;
  isDisabled?: boolean;
  transition: TypeTransitionProps;
  onActivated: () => void;
  onStepChange: (step: number) => void;
}

export const BaSeStepHeader: React.FC<StepHeaderProps> = ({
  title,
  iconName,
  steps,
  transition,
  currentStep,
  orientation,
  isActive = false,
  withError = false,
  isClickable = true,
  isCompleted = false,
  isDisabled = false,
  activeColor = BaSeTheme.colors.institucionais.azulSebrae36,
  completedColor = BaSeTheme.colors.feedbackInterface.sucesso45,
  uncompletedColor = BaSeTheme.colors.institucionais.cinzaSebrae60,
  activeWithErrorColor = BaSeTheme.colors.feedbackInterface.atencao35,
  completedWithErrorColor = BaSeTheme.colors.feedbackInterface.erro45,
  uncompletedWithErrorColor = BaSeTheme.colors.feedbackInterface.atencao55,
  tooltip = 'none',
  onActivated,
  onStepChange,
}) => {
  const [isTooltipOpen, setIsTooltipOpen] = React.useState(false);
  const itemRef = React.useRef<HTMLButtonElement>(null);

  const { getMessage } = React.useContext(BaSeI18nContext);

  const renderIcon = React.useCallback(
    (name: string, color?: string) => {
      const description = getMessage(
        withError ? 'stepCurrent.erro' : 'stepCurrent.description',
        currentStep.toString(),
      );
      return (
        <FadableItem>
          <BaSeIcon
            name={name}
            description={description}
            color={color ? color : isActive ? activeColor : uncompletedColor}
            colorTransition="fast"
            size={24}
          />
        </FadableItem>
      );
    },
    [
      withError,
      currentStep,
      isActive,
      activeColor,
      uncompletedColor,
      getMessage,
    ],
  );

  const renderText = React.useCallback(
    (stringOrList?: string | string[]) => {
      const texts = !Array.isArray(stringOrList)
        ? [stringOrList]
        : stringOrList;

      return (
        <StepText
          isActive={isActive}
          isCompleted={isCompleted}
          activeColor={activeColor}
          completedColor={completedColor}
          uncompletedColor={uncompletedColor}
          orientation={orientation}
          activeWithErrorColor={activeWithErrorColor}
          completedWithErrorColor={completedWithErrorColor}
          uncompletedWithErrorColor={uncompletedWithErrorColor}
          withError={withError}
        >
          <div>
            {texts.map(
              (text, index) =>
                text && <BaSeSmall1 key={index}>{text}</BaSeSmall1>,
            )}
          </div>
        </StepText>
      );
    },
    [
      isActive,
      isCompleted,
      activeColor,
      completedColor,
      uncompletedColor,
      orientation,
      activeWithErrorColor,
      completedWithErrorColor,
      uncompletedWithErrorColor,
      withError,
    ],
  );

  const resumeOnClosed = React.useMemo(
    () =>
      !isActive && isCompleted
        ? steps[currentStep - 1]?.resumeOnClosed
        : undefined,
    [isActive, isCompleted, steps, currentStep],
  );

  return (
    <StepContainer
      isActive={isActive}
      isCompleted={isCompleted}
      activeColor={activeColor}
      completedColor={completedColor}
      uncompletedColor={uncompletedColor}
      orientation={orientation}
      activeWithErrorColor={activeWithErrorColor}
      completedWithErrorColor={completedWithErrorColor}
      uncompletedWithErrorColor={uncompletedWithErrorColor}
      withError={withError}
    >
      <StepItem
        ref={itemRef}
        isActive={isActive}
        isCompleted={isCompleted}
        activeColor={activeColor}
        completedColor={completedColor}
        uncompletedColor={uncompletedColor}
        activeWithErrorColor={activeWithErrorColor}
        completedWithErrorColor={completedWithErrorColor}
        uncompletedWithErrorColor={uncompletedWithErrorColor}
        withError={withError}
        onMouseEnter={() => setIsTooltipOpen(true)}
        onMouseLeave={() => setIsTooltipOpen(false)}
        disabled={isDisabled}
        isClickable={isClickable}
        onClick={() => onActivated()}
        role={isClickable ? 'button' : 'heading'}
        tabIndex={isClickable ? 0 : -1}
        orientation={orientation}
      >
        <StepCircleHeader
          activeColor={activeColor}
          completedColor={completedColor}
          uncompletedColor={uncompletedColor}
          isActive={isActive}
          isCompleted={isCompleted}
          activeWithErrorColor={activeWithErrorColor}
          completedWithErrorColor={completedWithErrorColor}
          uncompletedWithErrorColor={uncompletedWithErrorColor}
          withError={withError}
        >
          {isCompleted
            ? renderIcon(
                withError ? 'close' : 'check',
                withError ? completedWithErrorColor : completedColor,
              )
            : withError
              ? renderIcon('exclamation', uncompletedWithErrorColor)
              : !!iconName
                ? renderIcon(iconName)
                : renderText(currentStep?.toString?.())}
        </StepCircleHeader>
        {renderText(title)}
      </StepItem>

      {tooltip !== 'none' && title && (
        <BaSeTooltip
          buddyRef={itemRef}
          color="outlined"
          direction={tooltip}
          message={title}
          open={isTooltipOpen}
        />
      )}

      {resumeOnClosed && (
        <StepResumeOnClosed hasIndentation={orientation === 'vertical'}>
          <BaSeSmall1 color={uncompletedColor}>{resumeOnClosed}</BaSeSmall1>
        </StepResumeOnClosed>
      )}

      {isActive && orientation === 'vertical' && (
        <StepperContentToRender
          orientation={orientation}
          transition={transition}
          steps={steps}
          currentStep={currentStep}
          onStepChange={onStepChange}
        />
      )}
    </StepContainer>
  );
};

BaSeStepHeader.displayName = 'BaSeStepHeader';
